import React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import {
  CssBaseline,
  Divider,
  IconButton,
  List,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { frontVersionInfo } from "../utils/util";
const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  backgroundColor: "#202833",
  color: "#fff",
});

const permanentStyles = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  boxSizing: "border-box",
  backgroundColor: "#202833",
  color: "#fff",
  borderRight: "none",
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const PermanentDrawer = styled(MuiDrawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": permanentStyles(theme),
}));

export interface SidebarProps {
  open: boolean;
  handleToggle: () => void;
  navItems: { text: string; link: string; icon?: React.ReactNode }[];
}

const Sidebar: React.FC<SidebarProps> = ({ open, handleToggle, navItems }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const logo = (
    <Box
      component={RouterLink}
      to="/"
      sx={{
        display: "flex",
        alignItems: "center",
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <Box
        component="img"
        src="/assets/logo.svg"
        alt="Device Sure Logo"
        sx={{ width: 40, height: 40, ml: 1, mr: 1 }}
      />
      <Typography variant="h6">DeviceSure</Typography>
    </Box>
  );

  const header = (
    <DrawerHeader>
      {logo}
      {isMobile && (
        <IconButton onClick={handleToggle} sx={{ color: "#fff" }}>
          <ChevronLeftIcon />
        </IconButton>
      )}
    </DrawerHeader>
  );

  const navList = (
    <List>
      {navItems.map((item, index) => {
        const isActive =
          item.link === "/"
            ? location.pathname === item.link
            : location.pathname === item.link ||
              location.pathname.startsWith(`${item.link}/`);
        return (
          <ListItemButton
            key={index}
            component={RouterLink}
            to={item.link}
            onClick={isMobile ? handleToggle : undefined}
            sx={{
              mx: 1,
              my: 0.25,
              px: 1.5,
              py: 1.25,
              borderRadius: 1,
              backgroundColor: isActive ? "rgba(233, 103, 45, 0.14)" : "inherit",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
            }}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  color: isActive ? "#FFB083" : "#B8C2CC",
                  minWidth: 38,
                }}
              >
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 700 : 600 }}
              sx={{ color: isActive ? "#FFFFFF" : "#B8C2CC" }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );

  const footer = (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography variant="caption" display="block" sx={{ color: "#B0BEC5" }}>
        Version {frontVersionInfo.version_number}
      </Typography>
      <Typography variant="caption" display="block" sx={{ color: "#B0BEC5" }}>
        © {new Date().getFullYear()} DeviceSure
      </Typography>
    </Box>
  );

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CssBaseline />
      {header}
      <Divider />
      <Box sx={{ flexGrow: 1 }}>{navList}</Box>
      <Divider />
      {footer}
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={open}
          onClose={handleToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              backgroundColor: "#202833",
              color: "#fff",
            },
          }}
        >
          {content}
        </MuiDrawer>
      ) : (
        <PermanentDrawer variant="permanent" open={true}>
          {content}
        </PermanentDrawer>
      )}
    </>
  );
};

export default Sidebar;
